import {Button, Image, Modal, notification, Popconfirm, Switch, Tag} from "antd";
import {ProductBaseDto} from "../../api";
import {useTranslation} from "react-i18next";
import {useEffect, useMemo, useState} from "react";
import {CameraFilled, DeleteFilled, SyncOutlined} from "@ant-design/icons";
import {useFilePicker} from "use-file-picker";
import {FileAmountLimitValidator, FileSizeValidator} from "use-file-picker/validators";

export interface ProductMediaData {
    image: File | string | undefined;
    deleteImage: boolean;
}

interface ProductMediaModalProps {
    product?: ProductBaseDto;
    isOpen: boolean;
    onConfirm: (data: ProductMediaData) => void;
    onSyncGamma: () => void;
    isSyncLoading?: boolean;
    onCancel: () => void;
}

const ProductMediaModal = ({product, isOpen, onConfirm, onSyncGamma, isSyncLoading = false, onCancel}: ProductMediaModalProps) => {
    const {t} = useTranslation();
    const [api, contextHolder] = notification.useNotification();

    const [mediaData, setMediaData] = useState<ProductMediaData>({
        image: undefined,
        deleteImage: false,
    });

    const {openFilePicker: selectImage, plainFiles: imageFile, filesContent: imageContent, clear: clearImage} = useFilePicker({
        readAs: 'DataURL',
        accept: 'image/*',
        multiple: false,
        validators: [
            new FileAmountLimitValidator({min: 1, max: 1}),
            new FileSizeValidator({maxFileSize: 5 * 1024 * 1024}),
        ],
        onFilesSelected: ({plainFiles, errors}) => {
            if (errors && errors.length > 0) return api.warning({message: t(`common.imagePickerError.${errors[0].reason}`)});
            setMediaData((d) => ({...d, image: plainFiles[0], deleteImage: false}));
        },
    });

    useEffect(() => {
        clearImage();
        setMediaData({image: product?.media?.path, deleteImage: false});
    }, [product]);

    const image = useMemo(() => {
        if (imageContent && imageContent.length > 0) return imageContent[0].content;
        if (!mediaData.deleteImage && typeof mediaData.image === "string") return mediaData.image;
        return null;
    }, [imageContent, mediaData]);

    const handleDeleteExisting = () => {
        clearImage();
        setMediaData({image: undefined, deleteImage: true});
    };

    return (
        <Modal
            title={
                <p className="text-[#0950A8] text-base font-semibold leading-6">
                    {product?.name}
                </p>
            }
            open={isOpen}
            onCancel={onCancel}
            footer={() => (
                <div className={"flex space-x-3 w-full justify-between items-center"}>
                    <Button
                        block
                        type={"default"}
                        className={"m-0 h-[40px] border border-solid border-[var(--Blue-400,#0950A8)] text-[#0950A8] text-sm font-bold"}
                        onClick={onCancel}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        block
                        type={"primary"}
                        className={"m-0 h-[40px] bg-[#0950A8] text-sm font-bold"}
                        onClick={() => onConfirm(mediaData)}
                    >
                        {t("common.saveChanges")}
                    </Button>
                </div>
            )}
        >
            {contextHolder}
            <hr className={"my-4 border-solid border-gray-100"}/>

            {/* Immagine */}
            <div className={"flex flex-col space-y-2"}>
                <div className="text-blue-400 text-md font-semibold leading-[18px]">
                    {t("common.articleImage")}
                </div>

                {image ? (
                    <div className={"relative w-full"}>
                        <Image
                            width={"100%"}
                            height={300}
                            src={image}
                            className={"border rounded-md"}
                            preview={false}
                        />
                        {imageFile && imageFile.length > 0 && (
                            <div
                                className={"absolute top-0 right-0 p-3 bg-white/90 rounded-tr-md rounded-bl-md group cursor-pointer shadow-md"}
                                onClick={clearImage}
                            >
                                <DeleteFilled className={"text-lg group-hover:scale-110 transition"}/>
                            </div>
                        )}
                        {!(imageFile && imageFile.length > 0) && typeof mediaData.image === "string" && (
                            <Popconfirm
                                title={t("common.confirmDelete")}
                                onConfirm={handleDeleteExisting}
                                okText={t("common.yes")}
                                cancelText={t("common.no")}
                            >
                                <div className={"absolute top-0 right-0 p-3 bg-white/90 rounded-tr-md rounded-bl-md group cursor-pointer shadow-md"}>
                                    <DeleteFilled className={"text-lg text-red-400 group-hover:scale-110 transition"}/>
                                </div>
                            </Popconfirm>
                        )}
                        <div
                            className={"absolute top-0 left-0 p-3 bg-white/90 rounded-tl-md rounded-br-md group cursor-pointer shadow-md"}
                            onClick={selectImage}
                        >
                            <CameraFilled className={"text-lg group-hover:scale-110 transition"}/>
                        </div>
                    </div>
                ) : (
                    <div
                        className={"flex items-center justify-center w-full h-[300px] border border-solid border-neutral-200 rounded-md"}
                    >
                        <CameraFilled
                            onClick={selectImage}
                            className={"text-4xl hover:text-blue-400 hover:scale-110 transition cursor-pointer"}
                        />
                    </div>
                )}
            </div>

            <hr className={"my-4 border-solid border-gray-100"}/>

            {/* Riepilogo dati prodotto */}
            <div className={"grid grid-cols-2 gap-y-4 gap-x-3"}>
                <div className={"flex flex-col space-y-1"}>
                    <div className="text-blue-400 text-xs font-semibold">{t("common.name") ?? "Nome"}</div>
                    <div className="text-[#2A2E34] text-sm capitalize">{product?.name?.toLocaleLowerCase()}</div>
                </div>

                <div className={"flex flex-col space-y-1"}>
                    <div className="text-blue-400 text-xs font-semibold">{t("common.code")}</div>
                    <div className="text-[#2A2E34] text-sm">{product?.code}</div>
                </div>

                <div className={"flex flex-col space-y-1"}>
                    <div className="text-blue-400 text-xs font-semibold">{t("common.brand")}</div>
                    <div className="text-[#2A2E34] text-sm">{product?.brand?.name}</div>
                </div>

                {product?.categories && product.categories.length > 0 && (
                    <div className={"flex flex-col space-y-1 col-span-2"}>
                        <div className="text-blue-400 text-xs font-semibold">{t("common.categories") ?? "Categorie"}</div>
                        <div className={"flex flex-wrap gap-1"}>
                            {product.categories.map((pc) => (
                                <Tag key={pc.id} className="rounded-lg text-xs">
                                    {pc.category?.father ? `${pc.category.father.name} › ` : ""}{pc.category?.name}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <hr className={"my-4 border-solid border-gray-100"}/>

            {/* Sincronizza Gamma */}
            <Button
                block
                icon={<SyncOutlined spin={isSyncLoading}/>}
                onClick={onSyncGamma}
                loading={isSyncLoading}
                disabled={isSyncLoading}
                className={"h-[40px] border border-solid border-orange-400 text-orange-500 font-semibold"}
            >
                Sincronizza Gamma
            </Button>

            <hr className={"my-4 border-solid border-gray-100"}/>
        </Modal>
    );
};

export default ProductMediaModal;
